import { forwardRef, type TextareaHTMLAttributes, useState } from 'react';
import { motion } from 'framer-motion';
import { cn } from '../../lib/utils';
import { VALIDATION } from '../../config';

interface TextAreaProps extends TextareaHTMLAttributes<HTMLTextAreaElement> {
  label?: string;
  error?: string;
  showCharCount?: boolean;
  maxLength?: number;
}

const TextArea = forwardRef<HTMLTextAreaElement, TextAreaProps>(
  (
    {
      className,
      label,
      error,
      showCharCount = true,
      maxLength = VALIDATION.maxAnswerLength,
      id,
      ...props
    },
    ref
  ) => {
    const [isFocused, setIsFocused] = useState(false);
    const charCount = (props.value as string)?.length || 0;

    return (
      <div className="w-full space-y-2">
        {label && (
          <label
            htmlFor={id}
            className="block text-sm font-semibold text-slate-100"
          >
            {label}
          </label>
        )}
        <div className="relative">
          <motion.div
            className={cn(
              'absolute inset-0 rounded-xl -z-10',
              isFocused && 'bg-primary-500/10'
            )}
            animate={{
              boxShadow: isFocused
                ? '0 0 0 2px rgba(37, 99, 235, 0.3)'
                : '0 0 0 0px rgba(37, 99, 235, 0)',
            }}
            transition={{ duration: 0.2 }}
          />
          <textarea
            ref={ref}
            id={id}
            className={cn(
              'w-full min-h-[88px] px-4 py-3 rounded-xl',
              'bg-slate-900/80 backdrop-blur-sm',
              'border border-slate-600',
              'text-slate-100 placeholder:text-slate-500',
              'resize-y transition-colors duration-200',
              'focus:outline-none focus:border-primary-500/50',
              error && 'border-red-500/50',
              className
            )}
            maxLength={maxLength}
            onFocus={() => setIsFocused(true)}
            onBlur={() => setIsFocused(false)}
            {...props}
          />
        </div>
        <div className="flex justify-between items-center text-xs">
          {error ? (
            <motion.span
              initial={{ opacity: 0, y: -4 }}
              animate={{ opacity: 1, y: 0 }}
              className="text-red-400"
            >
              {error}
            </motion.span>
          ) : (
            <span />
          )}
          {showCharCount && (
            <span
              className={cn(
                'text-slate-500 transition-colors',
                charCount > maxLength * 0.9 && 'text-yellow-500',
                charCount >= maxLength && 'text-red-400'
              )}
            >
              {charCount} / {maxLength}
            </span>
          )}
        </div>
      </div>
    );
  }
);

TextArea.displayName = 'TextArea';

export { TextArea };
